import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EnhancedRoute from 'containers/Route';
import { removeUrlParam } from './utils/helpers';

import { parseFacilityId } from 'utils/routes/parseParams';

import ErrorPage from 'pages/ErrorPage/ErrorPage';

import {
	ConfirmationPage,
	UnsubscribePage,
	HomePage,
	// SearchPage,
	SearchPageWrapper,
	UnitPageV2,
	LegalPage,
	LegalReviewPage,
	PaidLandingPage,
	MoveInConfirmationPage,
	StatePage, // use during the new state pages redesign
	StyleGuide,
	StyleGuidePage,
	VehicleStoragePage,
	ContactPage,
	ClimateControlledPage,
	PSPPage,
	ContactPageV2,
	EmailAuthPage,
	OMIPage,
	PrivacyPolicyPage,
	SizeGuidePage,
	StorageBasicsPage,
	StorageNearMePage,
} from 'pages/Bundles';

import { getBooking, updateMoveInConfirmation } from 'store/modules/bookings';
import {
	getHomepage,
	getStateLandingPage,
	getPaidLandingPage,
} from 'store/modules/pages';
import { getUnit } from 'store/modules/units';
import { getFacility } from 'store/modules/facilities';
import {
	getSearchPage,
	getCityLandingPage,
	getVehicleCityLandingPage,
	getAmenityCityLandingPage,
	getNearMePage,
} from 'store/modules/search';
import FacilityPageWrapper from './pages/FacilityPage/FacilityPageWrapper';

function ConfirmationPageWithAuth({ queryParams, match, ...rest }) {
	const params = useParams();
	const statusCode = useSelector((state) => state.bookings.statusCode);
	const confirmationCode =
		match?.params?.confirmationCode || params.confirmationCode;

	if ([401, 403].includes(statusCode)) {
		const { email, ...restQueryParams } = queryParams || {};
		if (email) {
			removeUrlParam('email');
		}
		return (
			<EmailAuthPage
				{...rest}
				queryParams={restQueryParams}
				match={match}
				confirmationCode={confirmationCode}
			/>
		);
	}

	return (
		<ConfirmationPage
			{...rest}
			statusCode={statusCode}
			queryParams={queryParams}
			match={match}
		/>
	);
}

ConfirmationPageWithAuth.propTypes = {
	queryParams: PropTypes.object,
	match: PropTypes.shape({
		params: PropTypes.shape({
			confirmationCode: PropTypes.string,
		}),
	}),
};

// Resolves /self-storage/:state/:city/:citySlug to either FacilityPage or SearchPageWrapper
// based on whether the slug ends with a numeric ID (facility) or not (vehicle/amenity search).
function CitySlugResolver(props) {
	const facilityId = parseFacilityId(props.match?.params?.citySlug);
	if (facilityId) {
		return <FacilityPageWrapper {...props} />;
	}
	return <SearchPageWrapper {...props} />;
}

CitySlugResolver.propTypes = {
	match: PropTypes.shape({
		params: PropTypes.shape({
			citySlug: PropTypes.string,
		}),
	}),
};

export const staticRoutes = [
	{
		path: '/',
		actions: { getHomepage },
		component: HomePage,
		loadData: (props) => props.actions.getHomepage(),
	},
	{
		path: '/contact-us',
		component: ContactPageV2,
	},
	{
		path: '/self-storage/:state',
		component: StatePage,
		actions: { getStateLandingPage },
		loadData: (props) =>
			props.actions.getStateLandingPage(props.match.params.state),
	},
	{
		path: '/self-storage/:state/:city',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getCityLandingPage },
		loadData: (props) =>
			props.actions.getCityLandingPage(
				props.match.params,
				props.queryParams,
				props.locationKey,
			),
	},
	{
		path: '/self-storage/:state/:city/:citySlug',
		component: CitySlugResolver,
		actions: {
			getVehicleCityLandingPage,
			getAmenityCityLandingPage,
			getFacility,
		},
		loadData: (props) => {
			const { citySlug } = props.match.params;
			// Facility: slug ends with numeric ID (e.g., "great-value-12345")
			const facilityId = parseFacilityId(citySlug);
			if (facilityId) {
				return props.actions.getFacility(
					facilityId,
					props.match.params.city,
					props.match.params.state,
					props.queryParams,
				);
			}
			// Vehicle: explicit allowlist of vehicle storage slugs
			const VEHICLE_SLUGS = ['car-storage', 'boat-storage', 'rv-storage'];
			if (VEHICLE_SLUGS.includes(citySlug)) {
				const vehicle = citySlug.replace(/-storage$/, '');
				return props.actions.getVehicleCityLandingPage(
					{ ...props.match.params, vehicle },
					props.queryParams,
					props.locationKey,
				);
			}
			// Amenity: everything else (e.g., "24-hour-access|drive-up-self-storage|free-truck-rental|climate-controlled")
			return props.actions.getAmenityCityLandingPage(
				{ ...props.match.params, amenity: citySlug },
				props.queryParams,
				props.locationKey,
			);
		},
	},
	{
		path: '/self-storage-near-me/:keyword',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getNearMePage },
		loadData: (props) =>
			props.actions.getNearMePage(
				props.match.params,
				props.queryParams,
				props.locationKey,
			),
	},
	{
		path: '/search',
		key: 'search',
		component: SearchPageWrapper,
		actions: { getSearchPage },
		loadData: (props) =>
			props.actions.getSearchPage(props.queryParams, props.locationKey),
	},
	{
		path: '/self-storage/:state/:city/ogf/:facilityPath',
		component: FacilityPageWrapper,
		actions: { getFacility },
		loadData: ({ actions, queryParams, match: { params } }) =>
			actions.getFacility(
				params.facilityId,
				params.city,
				params.state,
				queryParams,
			),
	},
	{
		path: '/facility/:facilityId',
		component: FacilityPageWrapper,
		actions: { getFacility },
		loadData: ({ actions, match: { params } }) =>
			actions.getFacility(params.facilityId),
	},
	{
		path: '/self-storage/:state/:city/:facilityPath/reserve/:unitId',
		component: UnitPageV2,
		actions: { getUnit },
		loadData: ({
			actions,
			match: {
				params: { facilityId, unitId },
			},
		}) => actions.getUnit(facilityId, unitId),
	},
	{
		path: '/reservation/confirm-move-in/:confirmationCode',
		component: MoveInConfirmationPage,
		actions: { updateMoveInConfirmation },
		moveInStatus: 'move-in',
		loadData: ({ actions, match: { params } }) =>
			actions.updateMoveInConfirmation(
				params.confirmationCode,
				params.moveInStatus,
			),
	},
	{
		path: '/reservation/confirm-no-move-in/:confirmationCode',
		component: MoveInConfirmationPage,
		actions: { updateMoveInConfirmation },
		moveInStatus: 'not-moved-in',
		loadData: ({ actions, match: { params } }) =>
			actions.updateMoveInConfirmation(
				params.confirmationCode,
				params.moveInStatus,
			),
	},
	{
		path: '/reservation/confirmation/:confirmationCode',
		actions: { getBooking },
		component: ConfirmationPageWithAuth,
		loadData: (props) =>
			props.actions.getBooking(
				props.match.params.confirmationCode,
				props.queryParams,
			),
	},
	{
		path: '/vehicle-storage',
		component: VehicleStoragePage,
	},
	{
		path: '/storage-units-near-me',
		component: StorageNearMePage,
	},
	{
		path: '/storage-units-with-climate-control',
		component: ClimateControlledPage,
	},
	{
		path: '/legal/consumer',
		component: LegalPage,
	},
	{
		path: '/legal/reviews',
		component: LegalReviewPage,
	},
	{
		path: '/legal/privacy',
		component: PrivacyPolicyPage,
	},
	{
		path: '/lpg/lf/:landingPage',
		component: PaidLandingPage,
		actions: { getPaidLandingPage },
		loadData: (props) =>
			props.actions.getPaidLandingPage(
				props.queryParams,
				props.match.url,
			),
	},
	{
		path: '/unsubscribe',
		component: UnsubscribePage,
	},
	{
		path: '/contact-selfstorage',
		component: ContactPage,
	},
	{
		path: '/what-to-expect',
		component: StorageBasicsPage,
	},
	{
		path: '/whattoexpect',
		component: StorageBasicsPage,
	},
	{
		path: '/ux',
		component: StyleGuide,
	},
	{
		path: '/style-guide',
		component: StyleGuidePage,
	},
	{
		path: '/reservation/online-move-in/:confirmationCode',
		component: OMIPage,
		actions: { getBooking },
		loadData: (props) =>
			props.actions.getBooking(
				props.match.params.confirmationCode,
				props.queryParams,
			),
	},
	{
		path: '/storage-types/:pspSlug',
		component: PSPPage,
	},
	{
		path: '/self-storage-sizes',
		component: SizeGuidePage,
	},
	{
		path: '/self-storage-sizes/:keyword',
		component: PSPPage,
	},
	{
		path: '*',
		component: ErrorPage,
	},
];

export default function AppRoutes({ ready }) {
	return (
		<Routes>
			{staticRoutes.map((route, i) => (
				<Route
					key={route.key || i}
					path={route.path}
					element={
						<EnhancedRoute
							component={route.component}
							actions={route.actions}
							loadData={route.loadData}
							moveInStatus={route.moveInStatus}
							ready={ready}
						/>
					}
				/>
			))}
		</Routes>
	);
}

AppRoutes.propTypes = {
	ready: PropTypes.bool,
};
